import clsx from "clsx"
import { useMemo } from "react"

import debounce from "@utils/debounce"

import { ExtraTouchArea, Skeleton } from "@particles"

import { Icon, Tooltip } from "@atoms"
import { IconSize } from "@atoms/Icon/Icon.types"

import useTabStack from "@molecules/TabStack/TabStack.store"
import { TabStackProps } from "@molecules/TabStack/TabStack.types"

import styles from "./TabItem.module.scss"
import { TabItemProps } from "./TabItem.types"

export default function TabItem(props: TabItemProps) {
    const {
        icon,
        iconActive,
        iconInReactNode,
        children,
        count,
        noDebounce,
        title,

        onClick,

        isLoading = false,
        isPressed = false,
        isDisabled = false,
        isTabbable = true,
        isSelected = false,
        _isFocused = false,
        _isHovered = false,
    } = props

    const { variant, size } = useTabStack()

    const iconSize = useMemo(() => {
        const sizeMap: { [key in TabStackProps["size"]]: IconSize } = {
            sm: 12,
            md: 14,
            lg: 14,
        }
        return sizeMap[size]
    }, [size])

    const desiredIcon = useMemo(() => {
        if (iconInReactNode) {
            return iconInReactNode
        } else if (iconActive && isSelected) {
            return <Icon icon={iconActive} size={iconSize} container="square" />
        } else if (icon) {
            return <Icon icon={icon} size={iconSize} container="square" />
        } else {
            return null
        }
    }, [icon, iconActive, iconInReactNode, isSelected, iconSize])

    const isIconOnly = useMemo(() => {
        return (icon || iconActive || iconInReactNode) && !children && count === undefined
    }, [icon, iconInReactNode, children, count])

    const skeletonWidth = useMemo(() => (size === "sm" ? 40 : 48), [size])

    return (
        <Tooltip body={title} isDisabled={!title}>
            <ExtraTouchArea extraTouchArea={variant === "underlined" ? 10 : 0} sides="horizontal">
                <button
                    onClick={
                        noDebounce
                            ? onClick
                            : debounce((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onClick(event))
                    }
                    type="button"
                    disabled={isDisabled || isLoading}
                    role="tab"
                    aria-selected={isSelected}
                    className={clsx(styles.base, styles[`${size}Size`], {
                        [styles.isLoading]: isLoading,
                        [styles.isPressed]: isPressed,
                        [styles.isSelected]: isSelected,
                        [styles.isFocused]: _isFocused,
                        [styles.isHovered]: _isHovered,
                        [styles.contained]: variant === "contained",
                        [styles.underlined]: variant === "underlined",
                        [styles.iconOnly]: isIconOnly,
                    })}
                    tabIndex={isTabbable ? 0 : -1}
                >
                    {isLoading ? (
                        isIconOnly ? (
                            <Skeleton circle={true} width={12} height={12} />
                        ) : (
                            <Skeleton borderRadius={0} width={skeletonWidth} />
                        )
                    ) : (
                        <>
                            {(icon || iconInReactNode) && desiredIcon}
                            {children && <div className={styles.content}>{children}</div>}
                            {count !== undefined && <div>{count}</div>}
                        </>
                    )}
                </button>
            </ExtraTouchArea>
        </Tooltip>
    )
}
